import { FLAGS } from '@gozoki/feature-flags'
import { CircularProgress } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Navigate, Outlet } from '@tanstack/react-router'
import { useState } from 'react'
import { AppHeader } from '~/components/new/app-ui/app-header'
import { Title } from '~/components/new/app-ui/title'
import { RbacAccessDenied } from '~/components/routes/administration/roles/access-denied'
import type { BoardTile } from '~/components/routes/project-tracking/board-data'
import { BoardDialogs } from '~/components/routes/project-tracking/board-tile-dialogs'
import { CurrentRequestsPanel } from '~/components/routes/project-tracking/current-requests-panel'
import { RecetteActionsProvider } from '~/components/routes/project-tracking/recette-actions'
import { RoadmapDialog } from '~/components/routes/project-tracking/roadmap-dialog'
import { RoadmapPanel } from '~/components/routes/project-tracking/roadmap-panel'
import { SynthesesPanel } from '~/components/routes/project-tracking/syntheses-panel'
import { useFeatureFlag, useFeatureFlagsReady } from '~/hooks/useFeatureFlag'
import { PROJECT_TRACKING_READ } from '~/lib/project-tracking'
import { api } from '~/lib/tuyau'
import { useMigratedRight, withRoutePermissions } from '~/utils/rbac'

// Route parente (pas un index) : elle rend le dashboard **et** un `Outlet`, pour que la fiche
// `$cardId` s'ouvre en modale par-dessus le board — y compris par deep-link à froid.
export const Route = createFileRoute('/_authenticated/project-tracking')({
  staticData: {
    permission: PROJECT_TRACKING_READ,
  },
  component: ProjectTrackingPage,
})
withRoutePermissions(Route)

function ProjectTrackingPage() {
  // Le `staticData.permission` ne discrimine rien tant que le flag `permission-v2` est OFF :
  // le droit v1 reste la seule garde effective de la page.
  const canRead = useMigratedRight(PROJECT_TRACKING_READ, ['PROJECT_TRACKING'])
  // Fallback fermé : Flipt injoignable ou flag absent ⇒ la page reste close.
  const featureEnabled = useFeatureFlag(FLAGS.projectTracking)
  const flagsReady = useFeatureFlagsReady()
  const [openTile, setOpenTile] = useState<BoardTile | null>(null)
  const [roadmapOpen, setRoadmapOpen] = useState(false)
  // Monté ici plutôt que dans les modales : ouvrir une vue développée ne relance aucun fetch.
  // `enabled` suit la garde — sinon une page fermée déclencherait quand même l'appel, que le
  // backend refuse en 404 puisqu'il porte le même flag.
  const { data: board } = useQuery({
    ...api.projectTracking.board.queryOptions(),
    enabled: featureEnabled && canRead,
  })

  // Tant que Flipt charge, le flag rend son fallback fermé : rediriger ici expulserait la page
  // avant qu'elle connaisse la réponse, et le `replace` est sans retour.
  if (!flagsReady) {
    return <CircularProgress />
  }

  // Flag OFF → pas d'accès direct par URL, comme `hr/collaborators` et `hr/planning.dashboard` : la
  // page n'existe pas, la racine choisit une destination. Le message reste réservé au RBAC, qui
  // répond à une autre question — « vous n'y avez pas droit » plutôt que « ça n'existe pas ».
  // Les deux retours couvrent aussi la fiche `$cardId`, rendue dans l'`Outlet` ci-dessous.
  if (!featureEnabled) {
    return <Navigate to="/" replace />
  }

  if (!canRead) {
    return <RbacAccessDenied />
  }

  return (
    <RecetteActionsProvider>
      <AppHeader>
        <Title>Suivi projet</Title>
      </AppHeader>

      {/* Le rythme de la pile est posé ici, pas sur chaque panneau : `AppHeader` porte déjà son
          `mb-2`, et le panneau « Planning de l'équipe » viendra s'ajouter sans rien à retoucher. */}
      <div className="flex flex-col gap-3">
        <CurrentRequestsPanel onOpen={setOpenTile} />

        {/* Panneau et modale montent leurs propres queries : rendus sous les deux gardes ci-dessus,
            ils ne déclenchent aucun appel quand la page est fermée par le flag ou le RBAC. */}
        <div className="flex flex-col gap-3 xl:flex-row">
          <div className="flex min-w-0 empty:hidden xl:grow xl:basis-[846px]">
            <RoadmapPanel onOpenAllChantiers={() => setRoadmapOpen(true)} />
          </div>
          <div className="flex min-w-0 xl:grow xl:basis-[470px]">
            <SynthesesPanel />
          </div>
        </div>
      </div>

      <BoardDialogs
        board={board}
        tile={openTile}
        onOpenTile={setOpenTile}
        onClose={() => setOpenTile(null)}
      />

      <RoadmapDialog open={roadmapOpen} onClose={() => setRoadmapOpen(false)} />

      {/* La fiche ticket, route enfant rendue en modale au-dessus du board. */}
      <Outlet />
    </RecetteActionsProvider>
  )
}
